import { useEffect, useState, ChangeEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  OutlinedInput,
  Pagination,
  Stack,
} from '@mui/material';
import { toast } from 'react-toastify';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { readAdminWallets, updateAdminWallet, deleteAdminWallet } from '@/api/stake-db';
import { AdminWallet, ReadAdminWalletsResponse, UpdateAdminWalletRequest } from '@/types/stake-db';
import { Tables } from '@/component/pools/table/Tables';
import { AdminWalletTableColumns } from '../table/StakeDbColumnsData';

interface AdminWalletListProps {
  refreshKey?: number;
}

export default function AdminWalletList({ refreshKey }: AdminWalletListProps) {
  const [list, setList] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const limit = 10;

  // 수정 다이얼로그
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminWallet | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });

  // 삭제 다이얼로그
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminWallet | null>(null);

  const fetchWallets = async () => {
    setLoading(true);
    try {
      const re = await readAdminWallets({ page, limit });
      if (re.status !== 200) return;

      const response: ReadAdminWalletsResponse = re.data;
      const wallets = response.wallets.map((item: AdminWallet) => ({
        ...item,
        id: item.walletId,
        isActiveText: item.isActive ? '활성' : '비활성',
        createdAtFormatted: item.createdAt || '-',
      }));
      setList(wallets);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Failed to fetch admin wallets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallets();
  }, [page, refreshKey]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // 수정
  const handleEditClick = (row: AdminWallet, event: React.MouseEvent) => {
    event.stopPropagation();
    setEditTarget(row);
    setEditForm({ name: row.name || '', description: row.description || '' });
    setEditOpen(true);
  };

  const handleEditChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setEditForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEditSubmit = async () => {
    if (!editTarget) return;

    try {
      const data: UpdateAdminWalletRequest = {
        walletId: editTarget.walletId,
        name: editForm.name || undefined,
        description: editForm.description || undefined,
      };

      const re = await updateAdminWallet(data);
      if (re.status === 200) {
        toast.success(re.data.message || '지갑 수정 성공');
        setEditOpen(false);
        fetchWallets();
      } else {
        toast.error(re.data?.message || '지갑 수정 실패');
      }
    } catch (error) {
      console.error('Update wallet error:', error);
      toast.error('지갑 수정 실패');
    }
  };

  // 삭제
  const handleDeleteClick = (row: AdminWallet, event: React.MouseEvent) => {
    event.stopPropagation();
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      const re = await deleteAdminWallet(deleteTarget.walletId);
      if (re.status === 200) {
        toast.success(re.data.message || '지갑 삭제 성공');
        setDeleteOpen(false);
        fetchWallets();
      } else {
        toast.error(re.data?.message || '지갑 삭제 실패');
      }
    } catch (error) {
      console.error('Delete wallet error:', error);
      toast.error('지갑 삭제 실패');
    }
  };

  const columnsWithActions: GridColDef[] = [
    ...AdminWalletTableColumns,
    {
      minWidth: 180,
      field: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => handleEditClick(params.row, e)}
          >
            수정
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={(e) => handleDeleteClick(params.row, e)}
          >
            삭제
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
        {!loading && (
          <Tables
            list={list}
            columns={columnsWithActions}
            getRowId={(row) => row.walletId}
          />
        )}
      </Box>

      <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Stack>

      {/* 수정 다이얼로그 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>지갑 정보 수정</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth disabled>
              <InputLabel>Address</InputLabel>
              <OutlinedInput
                value={editTarget?.address || ''}
                label="Address"
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Name</InputLabel>
              <OutlinedInput
                value={editForm.name}
                label="Name"
                onChange={handleEditChange('name')}
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Description</InputLabel>
              <OutlinedInput
                value={editForm.description}
                label="Description"
                onChange={handleEditChange('description')}
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleEditSubmit}>수정</Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>지갑 삭제 확인</DialogTitle>
        <DialogContent>
          <b>{deleteTarget?.address}</b> 지갑을 삭제하시겠습니까?
          <br />
          이 작업은 되돌릴 수 없습니다.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>취소</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>삭제</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
